import React, { useState } from "react";
import PageHero from "../components/PageHero";
import CTABanner from "../components/CTABanner";
import { NAICS_CODES, CERTIFICATIONS, COMPANY } from "../data/site";
import { Download, FileText, ShieldCheck, Handshake, ClipboardCheck, Users, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function GovernmentContracting() {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const res = await fetch(`${BACKEND_URL}/api/capability-statement`);
      if (!res.ok) throw new Error("Failed to generate PDF");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "StreetSociety_Capability_Statement.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Capability statement downloaded");
    } catch (e) {
      toast.error("Download failed. Please contact us directly.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div data-testid="page-government-contracting">
      <PageHero
        eyebrow="Government Contracting"
        title="Positioned to support federal, state, local, and commercial procurement needs."
        intro="Street Society LLC is structured for responsive service delivery, compliance awareness, and dependable contract execution — across direct awards, set-asides, teaming arrangements, and subcontracts."
        image="govBuilding"
      />

      {/* Entity Data */}
      <section className="section-y bg-white" data-testid="gov-entity">
        <div className="container-x">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <p className="text-overline text-[#4B7055] mb-4">Entity Information</p>
              <h2 className="heading-section text-3xl sm:text-4xl text-[#0A1F35]">
                Verified, registered, and procurement-ready.
              </h2>
              <p className="mt-5 text-[#4A5568] leading-relaxed">
                All identifiers, codes, and designations contracting officers and procurement
                officials need — in one place.
              </p>
            </div>

            <div className="lg:col-span-7">
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
                {[
                  ["UEI", COMPANY.uei],
                  ["CAGE Code", COMPANY.cage],
                  ["Registration", "SAM.gov Registered"],
                  ["Entity Type", "Limited Liability Company"],
                  ["Phone", COMPANY.phone],
                  ["Email", COMPANY.email],
                ].map(([k, v]) => (
                  <div key={k} className="bg-white p-6">
                    <dt className="text-overline text-[#4A5568]">{k}</dt>
                    <dd className="font-serif text-lg text-[#0A1F35] font-bold mt-1.5 break-all">
                      {v}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
      </section>

      {/* NAICS */}
      <section className="section-y bg-[#F5F7F9]" data-testid="gov-naics">
        <div className="container-x">
          <div className="max-w-2xl mb-10">
            <p className="text-overline text-[#4B7055] mb-3">NAICS Codes</p>
            <h2 className="heading-section text-3xl sm:text-4xl text-[#0A1F35]">
              Active NAICS coverage — with room to grow.
            </h2>
            <p className="mt-4 text-[#4A5568] leading-relaxed">
              Street Society maintains NAICS classifications that reflect current service areas. Our
              service model and entity structure are positioned to expand classification coverage as
              client and contract demand evolves.
            </p>
          </div>

          <div className="bg-white border border-[#E2E8F0]">
            <div className="grid grid-cols-12 px-6 py-4 border-b border-[#E2E8F0] text-overline text-[#4A5568]">
              <div className="col-span-3 sm:col-span-2">NAICS</div>
              <div className="col-span-9 sm:col-span-10">Description</div>
            </div>
            {NAICS_CODES.map((n) => (
              <div
                key={n.code}
                className="grid grid-cols-12 px-6 py-5 border-b border-[#E2E8F0] last:border-0 items-center hover:bg-[#F5F7F9] transition-colors"
                data-testid={`naics-row-${n.code}`}
              >
                <div className="col-span-3 sm:col-span-2 font-serif text-lg text-[#0A1F35] font-bold">
                  {n.code}
                </div>
                <div className="col-span-9 sm:col-span-10 text-sm text-[#1A2530]">{n.title}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Readiness pillars */}
      <section className="section-y bg-white" data-testid="gov-readiness-grid">
        <div className="container-x">
          <div className="max-w-2xl mb-12">
            <p className="text-overline text-[#4B7055] mb-3">Contract Readiness</p>
            <h2 className="heading-section text-3xl sm:text-4xl text-[#0A1F35]">
              Built for the operational realities of contract work.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
            {[
              {
                icon: ClipboardCheck,
                title: "Contract Readiness",
                desc: "Documented procedures, clear scopes, and accountable execution from kickoff to closeout.",
              },
              {
                icon: ShieldCheck,
                title: "Compliance-Focused Operations",
                desc: "Procurement standards, recordkeeping, and reporting designed for oversight environments.",
              },
              {
                icon: Handshake,
                title: "Prime Contractor Teaming",
                desc: "Open to teaming on relevant pursuits — supporting primes with scoped capability areas.",
              },
              {
                icon: Users,
                title: "Subcontracting Support",
                desc: "Reliable subcontract execution for facility, grounds, mechanical, and operational scopes.",
              },
            ].map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  className="bg-white p-8 card-hover"
                  data-testid={`gov-pillar-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <span className="inline-flex h-11 w-11 items-center justify-center bg-[#0A1F35] text-white mb-5">
                    <Icon size={20} strokeWidth={1.75} />
                  </span>
                  <h3 className="font-serif text-lg font-bold text-[#0A1F35] mb-2">{p.title}</h3>
                  <p className="text-sm text-[#4A5568] leading-relaxed">{p.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="section-y bg-[#0A1F35] text-white" data-testid="gov-certifications">
        <div className="container-x">
          <div className="max-w-2xl mb-10">
            <p className="text-overline text-[#9BB7A2] mb-3">Certifications & Designations</p>
            <h2 className="heading-section text-3xl sm:text-4xl text-white">
              Supporting set-aside, diverse, and small business goals.
            </h2>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {CERTIFICATIONS.map((c) => (
              <li
                key={c}
                className="px-5 py-4 border border-white/15 bg-white/[0.04] text-sm text-white/90 hover:border-[#9BB7A2] transition-colors"
              >
                {c}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Capability Statement */}
      <section className="section-y bg-white" data-testid="capability-statement-section">
        <div className="container-x">
          <div className="border border-[#E2E8F0] bg-[#F5F7F9] p-8 sm:p-12 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-2 flex lg:justify-center">
              <span className="inline-flex h-16 w-16 items-center justify-center bg-[#0A1F35] text-white">
                <FileText size={26} strokeWidth={1.5} />
              </span>
            </div>
            <div className="lg:col-span-7">
              <p className="text-overline text-[#4B7055] mb-2">Capability Statement</p>
              <h3 className="font-serif text-2xl sm:text-3xl text-[#0A1F35] font-bold">
                Download the Street Society LLC Capability Statement.
              </h3>
              <p className="mt-3 text-[#4A5568] text-sm sm:text-base">
                A one-page document with entity data, NAICS coverage, core capabilities, differentiators,
                and certifications — suitable for sharing with contracting officers and prime partners.
              </p>
            </div>
            <div className="lg:col-span-3 flex lg:justify-end">
              <button
                type="button"
                onClick={handleDownload}
                disabled={downloading}
                className="btn-base btn-primary disabled:opacity-70"
                data-testid="download-capability-statement"
              >
                {downloading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Preparing…
                  </>
                ) : (
                  <>
                    Download PDF <Download size={16} />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </section>

      <CTABanner />
    </div>
  );
}
